import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import {
  Save,
  Loader2,
  ArrowLeft,
  Clock,
  Smartphone,
  MessageCircle,
  Phone,
  ChevronRight,
  LayoutGrid,
  Zap,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ConversionEditor = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const isDirtyRef = useRef(false);

  const [config, setConfig] = useState({
    enable_sticky_bar: true,
    enable_program_selector: true,
    sticky_buttons: {
      whatsapp_text: '',
      enroll_text: '',
      call_text: '',
    },
    updated_at: null,
  });

  const updateConfig = useCallback((updater) => {
    setConfig(prev => typeof updater === 'function' ? updater(prev) : updater);
    setIsDirty(true);
    isDirtyRef.current = true;
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirtyRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  const handleNavigateBack = () => {
    if (isDirty && !window.confirm('You have unsaved changes. Leave anyway?')) return;
    navigate(-1);
  };

  useEffect(() => { fetchConfig(); }, []);

  const fetchConfig = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/landing/conversion-config`);
      if (res.ok) {
        const data = await res.json();
        setConfig(data);
        setIsDirty(false);
        isDirtyRef.current = false;
      }
    } catch (error) {
      toast.error('Failed to load config');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/landing/conversion-config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(config),
      });
      if (res.ok) {
        toast.success('Conversion settings saved');
        setIsDirty(false);
        isDirtyRef.current = false;
        fetchConfig();
      } else throw new Error();
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const updateStickyButton = (field, value) => {
    updateConfig(prev => ({
      ...prev,
      sticky_buttons: { ...prev.sticky_buttons, [field]: value }
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in max-w-3xl mx-auto p-6" data-testid="conversion-editor">
      {/* Header */}
      <div className="flex items-center justify-between sticky top-0 bg-slate-50 py-4 -mx-6 px-6 z-10 border-b">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={handleNavigateBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-heading font-bold text-slate-900">Conversion Tools</h1>
            <div className="flex items-center gap-3 text-sm text-slate-500">
              <span>Mobile sticky bar & audience selector</span>
              {config.updated_at && (
                <span className="flex items-center gap-1 text-xs text-slate-400">
                  <Clock className="w-3 h-3" />
                  Updated {new Date(config.updated_at).toLocaleDateString()}
                </span>
              )}
            </div>
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-primary" data-testid="save-conversion-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save
        </Button>
      </div>

      {/* Sticky Conversion Bar */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Smartphone className="w-5 h-5 text-primary" />
            Mobile Sticky Bar
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg">
            <Switch
              checked={config.enable_sticky_bar}
              onCheckedChange={v => updateConfig(p => ({ ...p, enable_sticky_bar: v }))}
            />
            <div>
              <Label className="font-medium">Enable Sticky Bar</Label>
              <p className="text-xs text-slate-500">Shows fixed bottom bar on mobile devices (&lt;1024px)</p>
            </div>
          </div>

          <p className="text-sm text-slate-500">
            Customize button labels. Leave empty to use defaults.
          </p>

          {/* Preview */}
          <div className="p-4 bg-slate-900 rounded-xl">
            <p className="text-xs text-slate-400 mb-3 text-center">Preview (Mobile)</p>
            <div className="flex items-stretch h-12 rounded-lg overflow-hidden">
              <div className="flex-1 flex items-center justify-center gap-2 bg-emerald-500 text-white text-sm font-medium">
                <MessageCircle className="w-4 h-4" />
                {config.sticky_buttons?.whatsapp_text || 'WhatsApp'}
              </div>
              <div className="flex-1 flex items-center justify-center gap-2 bg-primary text-white text-sm font-medium">
                {config.sticky_buttons?.enroll_text || 'Secure Your Spot'}
                <ChevronRight className="w-4 h-4" />
              </div>
              <div className="flex items-center justify-center gap-2 px-4 bg-slate-200 text-slate-700 text-sm font-medium">
                <Phone className="w-4 h-4" />
              </div>
            </div>
          </div>

          {/* Button Labels */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="flex items-center gap-2">
                <MessageCircle className="w-4 h-4 text-emerald-500" />
                WhatsApp Button
              </Label>
              <Input 
                value={config.sticky_buttons?.whatsapp_text || ''} 
                onChange={e => updateStickyButton('whatsapp_text', e.target.value)} 
                placeholder="WhatsApp"
              />
            </div>
            <div>
              <Label className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-primary" />
                Programs Button
              </Label>
              <Input 
                value={config.sticky_buttons?.enroll_text || ''} 
                onChange={e => updateStickyButton('enroll_text', e.target.value)} 
                placeholder="Secure Your Spot"
              />
            </div>
            <div>
              <Label className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-slate-600" />
                Call Button
              </Label>
              <Input 
                value={config.sticky_buttons?.call_text || ''} 
                onChange={e => updateStickyButton('call_text', e.target.value)} 
                placeholder="Call Now"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Program Audience Selector */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <LayoutGrid className="w-5 h-5 text-primary" />
            Program Audience Selector
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg">
            <Switch
              checked={config.enable_program_selector}
              onCheckedChange={v => updateConfig(p => ({ ...p, enable_program_selector: v }))}
            />
            <div>
              <Label className="font-medium">Enable Audience Selector</Label>
              <p className="text-xs text-slate-500">Shows "Who Are You Swimming For?" cards on homepage</p>
            </div>
          </div>

          <p className="text-sm text-slate-600">
            The audience selector displays 4 audience cards (Kids, Adults, Schools, Holiday) that link to filtered program views.
          </p>
        </CardContent>
      </Card>

      {/* Bottom Save */}
      <div className="flex justify-end pb-8">
        <Button onClick={handleSave} disabled={saving} size="lg" className="bg-primary">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save Conversion Settings
        </Button>
      </div>
    </div>
  );
};

export default ConversionEditor;
